import {PinAndExtras} from "../services/PinApi";
import {debounce as _debouce} from "./debounce";
import {transform} from "ol/proj";
import type {ProjectionLike} from "ol/proj";
import type Projection from "ol/proj/Projection";
import {Coordinate} from "ol/coordinate";
import {Feature, Map as OlMap, Observable} from "ol";
import {DEVICE_PIXEL_RATIO} from "ol/has";
import {Geometry, MultiPolygon, Polygon} from "ol/geom";
import {ObjectEvent} from "ol/Object";
import {htmlToPlaintext as _htmlToPlaintext} from "./htmlToPlainText";
import {uuidv4} from "./uuidv4";
import SimpleGeometry from "ol/geom/SimpleGeometry";

/**
 * Miscellaneous functions
 */
export module _F {
	export const debounce = _debouce;

	// @see https://gist.github.com/kakajika/959d1c8d31d3b065fe51
	export function flatMap<E, T>(ts: T[], f: (t: T) => Array<E>): Array<E> {
		return ts.reduce((ys: E[], x: T) => {
			return ys.concat(f(x));
		}, []);
	}

	/**
	 * Groups a list of items by keys produced by the function f
	 *
	 * E.g
	 * groupBy(['apple', 'orange', 'avocado'], (s) => s[0])
	 * {
	 * 	'a': ['apple', 'avocado'],
	 * 	'o': ['orange']
	 * }
	 *
	 * @param {T[]} ts
	 * @param {(t: T) => string} f
	 * @returns {object}
	 */
	export function groupBy<T>(ts: T[], f: (t: T) => string) : object {
		let result = {};
		ts.forEach((t) => {
			let u = f(t);
			if (!(u in result)) {
				result[u] = [];
			}
			result[u].push(t);
		});

		return result;
	}

	const VOID : any = {};

	/**
	 * Converts and EPSG string to an srid integer
	 *
	 * getSrid("EPSG:4326") === 4326
	 *
	 * @param proj
	 * @param defaultSrid
	 */
	export function getSrid(proj: ProjectionLike, defaultSrid: number = 4326) : number {
		const projAsString = typeof proj == 'string' ? <string> proj : (<Projection> proj).getCode();
		const srid = parseInt(projAsString.replace('EPSG:', ''));
		return isNaN(srid) ? defaultSrid : srid;
	}

    export function uniqueBy<T>(data : T[], f : (T) => string) : T[] {
        let check = {};
        return data.map((item) => {
            let transformed = f(item);
            if (!check[transformed]) {
                check[transformed] = true;
                return item;
            }
            return VOID;
        }).filter((i) => i !== VOID);
    }

	export function once<U>(f: () => U): (() => U) {
		let memo: U;
		return () => {
			if (f) {
				memo = f();
				f = null;
			}
			return memo;
		}
	}

    export function elemToAppendModal() {
		// if we're full screen, we need to append the modal to the element that is fullscreen,
		// otherwise it won't be shown.

		let fullscreenElement = angular.element(document.querySelector('.fullscreen'));
		if (fullscreenElement.length) {
			return fullscreenElement;
		}

		return angular.element(document.querySelector('body'));
	}

	export function maximum(ns: number[]) : number {
		return ns.reduce((maxSoFar, n) => maxSoFar > n ? maxSoFar : n, -Infinity);
    }

    export function maxBy<T>(ts: T[], f : (t: T) => number) : number {
		return maximum(ts.map(f));
    }

    export function arrayHead<T>(ts: T[], defaultValue : T = undefined) : T {
		return ts.length ? ts[0] : defaultValue;
    }

	export function pinTitleFromCoordinate(coordinate: Coordinate, projection: ProjectionLike = 'EPSG:2193') : string {
		const latLon = transform(coordinate, projection, 'EPSG:4326');
		return `Pin at ${latLon[1].toFixed(5)}, ${latLon[0].toFixed(5)}`;
	}

	export function pinColorFromFeature(f: Feature<Geometry|SimpleGeometry>, defaultColor: string = '#E14D40'): string {
		return f && f.get('hover') ? '#00AEEF' : f && f.get('selected') ? '#F7B62B' : defaultColor;
	}

	export function pinTitleFromFeature(f: Feature<Geometry|SimpleGeometry>): string {
		const maybePin: PinAndExtras = <any> f && f.get('pin');

		const titleFromPin = (maybePin && maybePin.data && maybePin.data.title);
		if (titleFromPin) return titleFromPin;

		const fields = f && f.get('fields');

		return(f && f.get('_title'))
			|| f.get('Name')
			|| f.get('name')
			|| (Array.isArray(fields) ? f.get(fields[0]) : undefined)
			|| '';
	}

	/**
	 * Like groupBy, but each input item may end up in multiple bins
	 *
	 *
	 * E.g
	 * function primeFactors(n) : number[] {
	 *  .... //returns the prime factors of n
	 *  }
	 *
	 *  primeFactors(4);
	 *  => [2]
	 *
	 *  primeFactors(6);
	 *  [2,3]
	 *
	 * groupByMany([2,3,4,5,6,7,8, 10, 12], primeFactors)
	 *
	 * => {
	 * 	'2': [2, 4, 6, 8, 10, 12],
	 * 	'3': [3, 6, 12],
	 * 	'5': [5, 10],
	 * 	'7': [7]
	 * }
	 *
	 * // Notice how, 6 has prime factors [2, 3] so 6 is grouped under 2 and 3.
	 *
	 * @param {T[]} ts
	 * @param {(t: T) => string[]} f
	 * @returns {object}
	 */
	export function groupByMany<T>(ts: T[], f: (t: T) => string[]) : object {
		let result = {};

		ts.forEach((t) => {
			let us = f(t);
			us.forEach((u) => {
				if (!(u in result)) {
					result[u] = [];
				}
				result[u].push(t);
			})
		});

		return result;
	}

	export function b64EncodeUnicode(str) {
		// first we use encodeURIComponent to get percent-encoded UTF-8,
		// then we convert the percent encodings into raw bytes which
		// can be fed into btoa.
		return btoa(encodeURIComponent(str).replace(/%([0-9A-F]{2})/g,
			function toSolidBytes(match, p1) {
				return String.fromCharCode(Number('0x' + p1));
			}));
	}

	export function b64DecodeUnicode(str) {
		// Going backwards: from bytestream, to percent-encoding, to original string.
		return decodeURIComponent(atob(str).split('').map(function(c) {
			return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
		}).join(''));
	}

	export type FactsheetType = 'smap' | 'smap-v2';

    export function getHectaresRounded(val : number) : number {
        let rounded = Math.round(val / 10000),
            figures = ("" + rounded).length;
        if (figures < 4) {
            return rounded;
        }
        if (figures < 6) {
            return Math.round(rounded / 10) * 10;
        }
        return Math.round(rounded / 100) * 100;
    }

	export function formatScale(scale: number): string {
		const fmt = new Intl.NumberFormat('en-NZ');
		return `1:${fmt.format(scale)}`;
	}

    export  function numberFormat(val) : string {
        let nStr = '' + val,
            x, x1, x2, rgx;
        x = nStr.split('.');
        x1 = x[0];
        x2 = x.length > 1 ? '.' + x[1] : '';
        rgx = /(\d+)(\d{3})/;
        while (rgx.test(x1)) {
            x1 = x1.replace(rgx, '$1' + ',' + '$2');
        }
        return x1 + x2;
    }
    export function percentFormat(val) : string {
        if (val < 0) {
            return '';
        }
        return (Number(val) * 100.0).toFixed(1) + "%";
    }

	export function sanitizeWMTSBaseURL(url: string, style : string = null) : string {
		if (style !== null) {
			url = url.replace('{style}', style);
		}
		return url.replace(/{/g, "%7B").replace(/}/g, "%7D");
	}

	export const htmlToPlaintext = _htmlToPlaintext;

	export function friendlyNZTMCoords(coord: Coordinate) : string {
		return Math.round(coord[0]) + " " + Math.round(coord[1]);
	}

	export function termPrefixSearch(theTerms: string[], candidate: string) {
		let tokens = candidate.trim()
			.split(/\s+/)
			.filter((s) => s.length);

		return tokens.some((token) => {
			return theTerms.some((term) => token.indexOf(term) == 0);
		});
		// return theTerms.some((t) => tokens.some((token) => token.indexOf(t) == 0));
	}

	export function paragraphise(text: string): string {
		text = text || '';
    	return text.split(/\r?\n|\r/)
			.map((l) => `<p>${l}</p>`)
			.join('');
	}

	/**
	 * Registers a long press listener on an {OlMap}
	 * Return a function that when called, will de-register the listener.
	 *
	 * @param {OlMap} map
	 * @param {(coordinate, event) => () => void} listener
	 */
	export function longPressListener(map: OlMap, listener: (coordinate, event) => void) : (() => void) {
    	let viewPort = map.getViewport();
		let timeoutId = undefined;

		let clear = () => {
			if (timeoutId !== undefined) {
				window.clearTimeout(timeoutId);
			}
		};

		let longPressListener = (e : TouchEvent) => {
			timeoutId = window.setTimeout(() => {
				if (e.touches.length == 1) {
					listener(map.getEventCoordinate(e as any), e);
				} else {
					clear();
				}
			}, 1000);
		};

    	viewPort.addEventListener('touchstart', longPressListener);

    	viewPort.addEventListener('touchmove', clear);
		viewPort.addEventListener('touchend', clear);
		let disableContext = (event) => {
			event.preventDefault();
			event.stopPropagation();
			return false;
		};

		viewPort.addEventListener('contextmenu', disableContext);

		return () => {
			viewPort.removeEventListener('touchstart', longPressListener);
			viewPort.removeEventListener('touchmove', clear);
			viewPort.removeEventListener('touchend', clear);
			viewPort.removeEventListener('contextmenu', disableContext);
		};
	}

	export function strokePattern(colour: string) {
		let canvas = document.createElement('canvas');
		let pixelRatio = DEVICE_PIXEL_RATIO;
		let w = 20 * pixelRatio;
		let h = 20 * pixelRatio;
		canvas.width = w;
		canvas.height = h;

		let ctx = canvas.getContext('2d');


		ctx.beginPath();
		ctx.strokeStyle = colour;
		ctx.lineWidth = 3;
		ctx.moveTo(-1, h+1);
		ctx.lineTo(w+1, -1);
		ctx.stroke();

		return ctx.createPattern(canvas, 'repeat');
	}

	export function getContrastYIQ(hexcolor : string): string {
		if (hexcolor[0] === '#')
			hexcolor = hexcolor.substr(1);
		const r = parseInt(hexcolor.substr(0, 2), 16);
		const g = parseInt(hexcolor.substr(2, 2), 16);
		const b = parseInt(hexcolor.substr(4, 2), 16);
		const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
		return (yiq >= 128) ? 'black' : 'white';
	}

	export function combineFeatures(features: Feature<Polygon|MultiPolygon>[]) : MultiPolygon|Polygon {
		if (features.length == 1) return <Polygon> features[0].getGeometry();

        let multiPoly = new MultiPolygon([]);
        features.forEach((f : Feature<Polygon|MultiPolygon>) => {
            let geom = f.getGeometry();
            if (geom instanceof Polygon) {
                multiPoly.appendPolygon(geom);
            } else if (geom instanceof MultiPolygon) {
                geom.getPolygons().forEach((aPoly) => multiPoly.appendPolygon(aPoly));
            }
        });
        return multiPoly;
    }

    export function colourToLegendChip(w: number, h: number, colour: string, canvas: HTMLCanvasElement = null, border: string = null, lineWidth = 1) : string {
        canvas = canvas || document.createElement('canvas');

        canvas.width = w;
        canvas.height = h;

        let ctx = canvas.getContext("2d");

        ctx.rect(0, 0, w, h);
        ctx.fillStyle = colour;
        ctx.fill();

        if (border) {
            ctx.strokeStyle = border;
            ctx.lineWidth = lineWidth;
            ctx.strokeRect(lineWidth, lineWidth, w-(lineWidth * 2), h-(lineWidth * 2));
        }

        return canvas.toDataURL();
    }

    export function extractEPSGNum(epsg: string, defaultValue: number = NaN) : number {
		try {
			return parseInt(epsg.split(':')[1])
		} catch (e) {
			console.warn(e);
        }

        return defaultValue;
    }

    type listener = (event: ObjectEvent) => any;

	export function arraysContainSameStuff<T>(as: T[], bs: T[]) {
        return as.length == bs.length
            && !as.some((e) => bs.indexOf(e) == -1)
            && !bs.some((e) => bs.indexOf(e) == -1);
    }

    export function oncePerChange(observable: Observable, f: (event: ObjectEvent) => void) : listener {
		let currentRevision = observable.getRevision();
		return ((event) => {
			let revision = observable.getRevision();
			if (revision > currentRevision) {
				try {
					f(event);
				} catch (e) {

                }
			}
			currentRevision = revision;
		});
	}

    /**
	 * returns a function that when called, will de-register the handler;
     * @param {() => any} f
     * @param {angular.IScope} $scope
     * @returns {(any) => void}
     */
	export function clickOutsideOfMapHandler(f: () => any, $scope: ng.IScope = null) : (() => void) {
        let once = false;

        let toApply = () => {
			f();
            angular.element(<any> window).off('click', onClickOut);
		};

        function onClickOut(ev) {
            if (once && !(ev.target instanceof HTMLCanvasElement)) {
            	if ($scope) {
            		$scope.$apply(toApply);
				} else {
            		toApply();
				}
            }
            once = true;
        }

        angular.element(<any> window).on('click', onClickOut);

        return () => angular.element(<any> window).off('click', onClickOut);
	}

	export const uuidvd = uuidv4;

	export function isMapPage() {
		return !!document.querySelector('.page-map');
	}

	export function isDefined(val: any): boolean {
		return val !== undefined && val !== null;
	}

	export function updateMapSizeWhenVisible(map) {
		const target = map && map.getTarget();
		const rect = target && target.getBoundingClientRect();
		if (!rect || !rect.width || !rect.height) {
			requestAnimationFrame(() => updateMapSizeWhenVisible(map));
		} else {
			map.updateSize();
		}
	}
}
